import { WidgetProps } from '../../models/widget.model';
import DataGrid from '../datagrid/DataGrid';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TagRepositoryRegisterPayload } from 'common/models/tag.model';
import { selectSelection } from '../../store/slices/selection.slice';
import { addTagRepository, selectTagEditorVisible } from '../../store/slices/tag-editor.slice';
import { ListResponse } from '../../store/api/chart.api';
import CardsGrid, { GridCardType } from '../cards-grid/CardsGrid';
import { selectViewStateForWidget } from '../../store/slices/view-toggle.slice';

import { formatAppDate } from '../../hooks/utils';
import { TagCell } from '../datagrid/TagCell';
import { TagEditor } from '../tags/TagEditor';
import useFeatureFlags from 'common/hooks/useFeatureFlags';
import { Feature } from 'common/models/page-config.models';
import { useGetResizableCulmnSize } from '../../../common/hooks/charts';
import { useWidgetSearchFilter } from 'common/components/widgets/widget-hooks/useWidgetSearchFilter';
import { AttachmentColumn } from './widget-subcomponents/Attachment';
import { DateCell } from '../datagrid/DateCell';
import ProfileNameCell from '../datagrid/ProfileNameCell';
import TeamMemberCell from '../datagrid/TeamMemberCell';
import TextCell from '../datagrid/TextCell';
import AlignmentTextCell from '../datagrid/AlignmentTextCell';
import { ColumnModel } from 'common/models/widget-config.model';

const cellWidth = '140px';

export const columns: ColumnModel[] = [
  {
    field: 'date',
    title: 'DATE',
    filter: 'date',
    sortable: true,
    width: '75px',
    groupable: true,
    cell: DateCell,
  },
  {
    sortable: true,
    title: 'TEAM MEMBER',
    field: 'fieldTeam',
    resizable: true,
    width: '150px',
    groupable: true,
    cell: TeamMemberCell,
  },
  {
    title: 'PROFILE',
    field: 'profile',
    width: '150px',
    sortable: true,
    resizable: true,
    groupable: true,
    cell: ProfileNameCell,
  },
  {
    sortable: true,
    title: 'Specialty',
    field: 'specialtyName',
    width: '100px',
    groupable: true,
  },
  {
    sortable: true,
    field: 'alignmentLabel',
    resizable: true,
    title: 'ALIGNMENT',
    width: cellWidth,
    filter: 'text',
    groupable: true,
    cell: AlignmentTextCell,
  },
  {
    sortable: true,
    title: 'Response',
    field: 'response',
    width: '220px',
    groupable: true,
    cell: TextCell,
  },
  {
    sortable: true,
    title: 'Response Details',
    field: 'responseDetail',
    width: '400px',
    resizable: true,
    groupable: false,
    cell: TextCell,
  },
  {
    sortable: false,
    title: 'Tags',
    field: 'tags',
    resizable: true,
    width: '400px',
    groupable: false,
    cell: TagCell,
  },
  {
    title: 'Category',
    field: 'survey',
    width: '220px',
    groupable: true,
  },
  {
    title: 'Question',
    field: 'question',
    groupable: true,
    width: '220px',
  },
  {
    sortable: true,
    title: 'Insight Source',
    field: 'insightSourceName',
    width: '220px',
    groupable: false,
    cell: TextCell,
  },
];

const card: GridCardType = {
  topRowParams: [
    {
      name: 'date',
      sequence: 1,
      callback: (date: any) => formatAppDate(new Date(date), 'MM/dd/yyyy'),
    },
    {
      name: 'profile',
      sequence: 2,
    },
    {
      name: 'specialtyName',
      sequence: 3,
    },
    {
      name: 'fieldTeam',
      sequence: 4,
    },
  ],
  descriptionTitle: 'response',
  descriptionBody: 'responseDetail',
  tags: 'tags',
};

const SELECTION_TYPE = 'response';

const VIEW = {
  GRID: 0,
  CARD_LIST: 1,
};

export default function WidgetAlignmentResponseDetail(props: Readonly<WidgetProps<ListResponse>>) {
  const dispatch = useDispatch();
  const { resizableCulmnSize } = useGetResizableCulmnSize();
  const selection = useSelector(selectSelection);
  const widgetViews = useSelector(selectViewStateForWidget);
  const tagEditorVisible = useSelector(selectTagEditorVisible);
  const viewIndex = widgetViews[props.id] ? widgetViews[props.id] : 0;
  const [renderKey, setRenderKey] = useState(new Date().getTime());
  const { hasFeature } = useFeatureFlags();
  const hasFileUpload = hasFeature([Feature.CaptureFileUpload, Feature.CaptureOrganicFileUpload]);

  const searchColumns = useMemo(() => {
    if (viewIndex === VIEW.GRID) {
      return [
        'date',
        'profile',
        'responseDetail',
        'responseOptions',
        'response',
        'fieldTeam',
        'tags',
        'alignmentLabel',
        'specialty',
      ];
    }
    return [
      'date',
      'profile',
      'response',
      'responseDetail',
      'responseOptions',
      'fieldTeam',
      'tags',
      'specialty',
    ];
  }, [viewIndex]);

  const { dataStateChangeWithTags, dataResult, dataState, totalCount } = useWidgetSearchFilter({
    searchColumns,
    widget: props.widget,
    data: props.data,
  });

  useEffect(() => {
    setRenderKey(new Date().getTime());
  }, [resizableCulmnSize]);

  useEffect(() => {
    const payload: TagRepositoryRegisterPayload = {
      repository: SELECTION_TYPE,
      records: {},
    };
    dataResult.data.forEach((row: any) => {
      payload.records[row.responseId] = row.tags;
    });
    dispatch(addTagRepository(payload));
  }, [dataResult, dispatch]);

  const dataResultWithSelection = {
    ...dataResult,
    data: dataResult.data.map((item: any): any => {
      return {
        ...item,
        selected: selection[SELECTION_TYPE]
          ? selection[SELECTION_TYPE].includes(item.responseId)
          : false,
        tagsCounter: Object.keys(item?.tags ?? {}).length,
      };
    }),
  };

  if (viewIndex === VIEW.GRID) {
    return (
      <>
        {tagEditorVisible && <TagEditor />}
        <DataGrid
          resizable={true}
          widgetId={props.id}
          className="treelist-grid-category-response"
          onDataStateChange={dataStateChangeWithTags}
          {...dataState}
          groupable={props.groupable}
          total={totalCount}
          sortable={true}
          data={dataResultWithSelection}
          columns={hasFileUpload ? [...columns, AttachmentColumn] : columns}
          key={renderKey}
        />
      </>
    );
  }

  return (
    <div className="widget-response-details">
      {tagEditorVisible && <TagEditor />}
      <div className="k-scrollable">
        <CardsGrid
          total={totalCount}
          widgetId={props.id}
          data={dataResultWithSelection}
          card={card}
        />
      </div>
    </div>
  );
}
